<template>
    <div>
        <page-header
            :text="$t(activeData.name)"
            :searchField="{
                placeholder: $t('common.search'),
                inputEmitEvent: 'search',
                allowedFields: activeData.searchableFields,
                defaultField: activeData.searchableFields[0],
                nameTranslation: activeData.nameTranslation ? activeData.nameTranslation : 'name',
            }"
            @search="searchInputHandler"
        />
        <page-subnavigation :items="organizationDetailLinks" />
        <div>
            <div v-if="activeData.addText && activeData.canAdd" class="row row--table row--blocks align-center">
                <div class="column-tablet-8 hide-until-tablet"></div>
                <div class="column-tablet-8 hide-until-tablet"></div>

                <div class="column-tablet-8">
                    <a class="button button--large" @click="createByType(activeData.key)">
                        <i class="icon icon--action-add"></i>
                        {{ $t(activeData.addText) }}
                    </a>
                </div>
            </div>
            <DataTable
                v-if="activeData.data && activeData.data.length > 0"
                :columns="activeData.headerData"
                :tableData="activeData.data"
                :searchedTableData="activeData.searchedData"
                :paginationData="activeData.paginationData"
                :activeSorting="activeData.activeSorting"
                @removeItem="removeItemHandler($event, activeData.key)"
                @roleChanged="roleChangedHandler($event, activeData.key)"
                @goToPage="pageChangeHandler($event, activeData)"
                @sortBy="sortClickHandler($event)"
            />
            <div v-else class="not-found">
                <h4>{{ $t('organizations.no_data_available') }}</h4>
            </div>
        </div>
    </div>
</template>

<script>
import * as api from '@/api';
import debounce from 'lodash/debounce';
import { mapState } from 'vuex';
import { createSearchParams } from '@/utils/searchParameters';
import PageHeader from '@/components/PageHeader';
import DataTable from '@/components/DataTable';
import eventBus from '@/utils/eventBus';
import PageSubnavigation from '@/components/PageSubnavigation.vue';
import generalLinks from '@/mixins/generalLinks';

export default {
    components: {
        PageHeader,
        DataTable,
        PageSubnavigation,
    },
    mixins: [generalLinks],
    props: {
        dataType: {
            type: String,
            default: 'person',
        },
    },
    data() {
        return {
            organizationId: false,
            subData: [
                {
                    key: 'person',
                    fetchApi: 'persons',
                    searchApi: 'searchPersons',
                    responseField: 'persons',
                    name: 'organizations.persons',
                    addText: 'organizations.add_person',
                    canAdd: this.$can('invite', 'Organization persons'),
                    headerData: [
                        {
                            name: 'partners.name',
                            field: 'name',
                            type: 'text',
                            sortable: true,
                        },
                        {
                            name: 'partners.email',
                            field: 'email',
                            type: 'text',
                            sortable: true,
                        },
                        {
                            name: 'partners.postcode',
                            field: 'postcode',
                            type: 'text',
                            sortable: true,
                        },
                        {
                            name: 'partners.city',
                            field: 'city',
                            type: 'text',
                            sortable: true,
                        },
                        {
                            name: 'partners.country',
                            field: 'country',
                            type: 'text',
                            sortable: true,
                        },
                        {
                            name: 'users_admin.sso',
                            field: 'sso',
                            type: 'yesno',
                            sortable: false,
                        },
                        {
                            name: 'organizations.role',
                            field: 'role',
                            type: 'select',
                            sortable: true,
                            disabled: {
                                ability: 'isNotOrganizationOwner',
                                fieldValue: 'owner',
                            },
                            options: [
                                {
                                    value: 'owner',
                                    label: 'organizations.role_owner',
                                    disabled: 'isNotOrganizationOwner',
                                },
                                {
                                    value: 'admin',
                                    label: 'organizations.role_admin',
                                },
                                {
                                    value: 'technician',
                                    label: 'organizations.role_technician',
                                },
                                {
                                    value: 'person',
                                    label: 'organizations.role_member',
                                },
                            ],
                            inputEmitEvent: 'roleChanged',
                        },
                        {
                            name: 'buttons.delete',
                            type: 'remove',
                            available: {
                                type: 'isOwnerOrHigher',
                                field: 'role',
                            },
                        },
                    ],
                    activeSorting: false,
                    data: false,
                    paginationData: false,
                    searchedData: false,
                    activeSearchTerm: false,
                    searchableFields: ['name', 'email', 'postcode', 'city', 'country'],
                },
                {
                    key: 'system-service',
                    fetchApi: 'servicedSystems',
                    searchApi: 'searchServicedSystems',
                    responseField: 'systems',
                    name: 'organizations.serviced_systems',
                    headerData: [
                        {
                            name: 'systems.system',
                            field: 'name',
                            type: 'link',
                            linkTo: {
                                name: 'system-detail',
                                linkParam: 'id',
                            },
                            sortable: true,
                        },
                        {
                            name: 'systems.address',
                            fields: ['address1', 'address2', 'postcode', 'city', 'country'],
                            type: 'text',
                        },
                        {
                            name: 'fields.labels',
                            field: 'labels',
                            type: 'labels',
                        },
                        {
                            name: 'systems.logs.message',
                            field: 'latest_alarm',
                            type: 'alarm-message',
                        },
                        // {
                        //     name: '',
                        //     type: 'remove',
                        //     available: true,
                        // },
                    ],
                    activeSorting: false,
                    data: false,
                    paginationData: false,
                    searchedData: false,
                    activeSearchTerm: false,
                    nameTranslation: 'system',
                    searchableFields: ['name', 'addr', 'label', 'errorcode'],
                },
                {
                    key: 'system-operate',
                    fetchApi: 'operatedSystems',
                    searchApi: 'searchOperatedSystems',
                    responseField: 'systems',
                    name: 'organizations.operated_systems',
                    headerData: [
                        {
                            name: 'systems.system',
                            field: 'name',
                            type: 'link',
                            linkTo: {
                                name: 'system-detail',
                                linkParam: 'id',
                            },
                            sortable: true,
                        },
                        {
                            name: 'systems.address',
                            fields: ['address1', 'address2', 'postcode', 'city', 'country'],
                            type: 'text',
                        },
                        {
                            name: 'fields.labels',
                            field: 'labels',
                            type: 'labels',
                        },
                        {
                            name: 'systems.logs.message',
                            field: 'latest_alarm',
                            type: 'alarm-message',
                        },
                        // {
                        //     name: '',
                        //     type: 'remove',
                        //     available: true,
                        // },
                    ],
                    activeSorting: false,
                    data: false,
                    paginationData: false,
                    searchedData: false,
                    activeSearchTerm: false,
                    nameTranslation: 'system',
                    searchableFields: ['name', 'addr', 'label', 'errorcode'],
                },
            ],
        };
    },
    computed: {
        ...mapState(['activeOrganization']),
        activeData() {
            return this.subData.find(data => data.key === this.dataType);
        },
    },
    beforeRouteUpdate(to, from, next) {
        this.organizationId = to.params.id;
        eventBus.$emit('clearSearch');
        this.loadSubdata();
        next();
    },
    beforeRouteEnter(to, from, next) {
        next(vm => {
            eventBus.$emit('clearSearch');
            vm.loadSubdata();
        });
    },
    created() {
        if (!this.organizationId) this.organizationId = this.$route.params.id;
    },
    methods: {
        // event handlers
        removeItemHandler(id, type) {
            this.showRemoveItemDialog(id, type);
        },
        roleChangedHandler(e) {
            this.updateRoleOfPerson(e);
        },
        searchInputHandler: debounce(function(e) {
            const searchTerm = e;
            if (searchTerm !== '') {
                this.activeData.activeSearchTerm = e;
                this.searchSubdata(e);
            } else {
                this.activeData.activeSearchTerm = false;
                this.activeData.searchedData = false;
                this.loadSubdata();
            }
        }, 300),
        pageChangeHandler(e) {
            const page = e;
            if (this.activeData.activeSearchTerm) {
                this.searchSubdata(this.activeData.activeSearchTerm, page);
            } else {
                this.loadSubdata(page);
            }
        },
        sortClickHandler(field) {
            if (this.activeData.activeSorting[field] === 'asc') {
                this.activeData.activeSorting = {};
                this.activeData.activeSorting[field] = 'desc';
            } else {
                this.activeData.activeSorting = {};
                this.activeData.activeSorting[field] = 'asc';
            }
            if (this.activeData.activeSearchTerm) {
                this.searchSubdata(this.activeData.activeSearchTerm, this.activeData.activePage);
            } else {
                this.loadSubdata(this.activeData.activePage);
            }
        },
        // load the data
        async loadSubdata(page = 1) {
            try {
                const params = { page: page };
                if (Object.keys(this.activeData.activeSorting).length > 0) {
                    const field = Object.keys(this.activeData.activeSorting)[0];
                    params.order = `${field}${this.activeData.activeSorting[field] === 'asc' ? '' : ' desc'}`;
                }
                const response = await api.organizations[this.activeData.fetchApi](this.organizationId, params);
                if (response && response.data) {
                    this.activeData.data = response.data[this.activeData.responseField];
                    this.activeData.paginationData = response.data._metadata;
                }
            } catch (error) {
                console.error(error);
            }
        },
        // search subdata
        async searchSubdata(searchTerm, page = 1) {
            const allowedParams = this.activeData.searchableFields;
            const searchParams = createSearchParams(searchTerm, allowedParams);
            const params = {
                ...searchParams,
                page: page,
            };
            if (Object.keys(this.activeData.activeSorting).length > 0) {
                const field = Object.keys(this.activeData.activeSorting)[0];
                params.order = `${field}${this.activeData.activeSorting[field] === 'asc' ? '' : ' desc'}`;
            }
            try {
                const response = await api.organizations[this.activeData.searchApi](this.organizationId, { params });
                if (response && response.data) {
                    this.activeData.searchedData = response.data[this.activeData.responseField];
                    this.activeData.paginationData = response.data._metadata;
                }
            } catch (error) {
                console.error(error);
            }
        },
        async updateRoleOfPerson(data) {
            const newValue = data.value;
            try {
                await api.organizations.updateRoleOfPerson(this.organizationId, data.id, {
                    role: newValue,
                });
                this.$notify({
                    type: 'success',
                    text: this.$t('organizations.role_update_successful'),
                });
            } catch (error) {
                this.$notify({
                    type: 'error',
                    text: this.$t('organizations.role_update_error'),
                });
                if (this.activeData.activeSearchTerm) {
                    this.searchSubdata(this.activeData.activeSearchTerm);
                } else {
                    this.loadSubdata();
                }
            }
        },
        createByType(type) {
            if (type === 'suborganization') {
                this.showCreateEditOrganizationDialog({
                    parentOrganizationId: this.organizationId,
                });
            } else if (type === 'person' || type === 'system-operate' || type === 'system-service') {
                this.showOrganizationInvitationDialog(type);
            }
        },
        showRemoveItemDialog(id, type) {
            let text = '';
            if (type === 'person') {
                text = this.$t('organizations.delete_person_text');
            } else if (type === 'suborganization') {
                text = this.$t('organizations.delete_suborganization_text');
            } else if (type.indexOf('system') >= 0) {
                text = this.$t('organizations.delete_system_text');
            }
            this.$modal.show(
                () => import('@/views/modal/ConfirmDeletion.vue'),
                {
                    text: text,
                    cancelText: this.$t('buttons.cancel'),
                    confirmText: this.$t('buttons.delete'),
                    successCallback: () => {
                        let request;
                        if (type === 'person') {
                            request = api.organizations.deletePerson(this.organizationId, id);
                        } else if (type === 'suborganization') {
                            request = api.organizations.delete(id);
                        } else if (type.indexOf('system') >= 0) {
                            request = api.organizations.deleteSystem(this.organizationId, id);
                        }

                        request
                            .then(response => {
                                if (response.data === '') {
                                    if (type === 'person' || type === 'suborganization' || type.indexOf('system') >= 0) {
                                        if (this.activeData.activeSearchTerm) {
                                            this.searchSubdata(this.activeData.activeSearchTerm);
                                        } else {
                                            this.loadSubdata();
                                        }
                                    }
                                }
                            })
                            .catch(error => {
                                const text = error.response.data !== '' ? error.response.data : this.$t('errors.error_occurred');
                                this.$notify({ type: 'error', text: text });
                            });
                    },
                },
                {
                    name: 'ConfirmDeletion',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                }
            );
        },
        showOrganizationInvitationDialog(type) {
            this.$modal.show(
                () => import('@/views/modal/OrganizationAdd.vue'),
                {
                    organizationId: this.organizationId,
                    invitationType: type,
                },
                {
                    name: 'OrganizationAdd',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                },
                {
                    'before-close': () => {
                        this.loadSubdata();
                    },
                }
            );
        },
        showCreateEditOrganizationDialog(data) {
            this.$modal.show(
                () => import('@/views/modal/CreateEditOrganization.vue'),
                data,
                {
                    name: 'CreateEditOrganization',
                    scrollable: true,
                    width: 450,
                    height: 'auto',
                    class: 'ngdialog-theme-winconnect',
                },
                {
                    'before-close': event => {
                        this.loadSubdata();
                    },
                }
            );
        },
    },
};
</script>
